'use client'

import React from 'react'
import { Dropdown } from 'react-bootstrap'
import { isMobile } from 'react-device-detect'
import styled, { css } from 'styled-components'
import DcTag from '../dc-tag/dc-tag'
import { $DCprimaryColor, $DCprimaryActiveColor, colors } from '../styleGuide'

export interface MenuProps {
  className?: string
  variant?: 'outline' | 'simple' | 'secondary' | 'link' | 'tag' | string
  size?: 'lg' | 'sm'
  active?: boolean
  disabled?: boolean
  margin?: string
  padding?: string
  tooltip?: string
  isGrouped?: boolean
  maxheight?: string
  toggleText?: boolean
  options?: OptionsData[]
  width?: string
}

interface OptionsData {
  label: string
  disabled?: boolean
  clickHandler?: () => void
  children?: OptionsData[]
  formatter?: (data: OptionsData) => React.ReactNode
}

const DropDownListItem = styled(Dropdown.Item)<{ isGrouped: boolean }>`
  inset: 0px 0px auto auto;
  ${(props) =>
    props.isGrouped
      ? css`
          & > div {
            & > div {
              & > a {
                &:active {
                  background-color: ${$DCprimaryColor};
                }
              }
            }
            & > button {
              outline: none !important;
              border: none !important;
              &:focus {
                box-shadow: none;
              }
            }
          }
          &:active {
            background-color: ${colors.grayBorderLight};
          }
        `
      : css`
          &:active {
            background-color: ${$DCprimaryColor};
          }
        `}
  ${(props: MenuProps) =>
    props.variant !== '' &&
    props.variant === null &&
    css`
      & > div {
        & > button {
          background-color: none;
          color: black;
          &:focus {
            box-shadow: none;
          }
        }
      }
      &:active {
        background-color: ${colors.grayBorderLight};
      }
    `}
`

const StyledInnerLine = styled(Dropdown.Toggle)`
  font-size: 12px;
`

const StyledPixelButton = styled(Dropdown.Toggle)`
  width: ${isMobile ? '100%' : 'auto'};
  &:focus {
    outline: none !important;
    box-shadow: none !important;
  }
  margin: ${(props: MenuProps) => props.margin || '0px'};
  a {
    color: white;
    text-decoration: none;
    &:hover {
      color: white;
    }
  }
  padding: ${(props: MenuProps) => props.padding};
  ${(props: MenuProps) =>
    props.variant === 'outline' &&
    css`
      background-color: transparent;
      border-color: ${$DCprimaryColor} !important;
      color: ${$DCprimaryColor} !important;
      outline: none;
    `}
  ${(props: MenuProps) =>
    props.variant === 'simple' &&
    css`
      background-color: ${$DCprimaryColor} !important;
      border-color: ${$DCprimaryColor} !important;
      color: ${colors.white} !important;
      &:hover,
      &:active,
      &:focus {
        background-color: ${$DCprimaryActiveColor} !important;
        border-color: ${$DCprimaryActiveColor} !important;
      }
    `}
    ${(props: MenuProps) =>
    props.variant === 'link' &&
    css`
      background-color: transparent;
      border-color: transparent;
      color: ${$DCprimaryColor};
      padding: ${(props: MenuProps) => props.padding || '0.375rem 0.75rem'};
      &:hover,
      &:hover a,
      &:active,
      &:active a,
      &:focus,
      &:focus a {
        color: ${$DCprimaryActiveColor} !important;
      }
      a {
        color: ${$DCprimaryColor} !important;
      }
    `}
    ${(props: MenuProps) =>
    props.active &&
    css`
      background-color: ${$DCprimaryActiveColor} !important;
      border-color: ${$DCprimaryActiveColor} !important;
    `}
    ${(props: MenuProps) =>
    props.variant === 'tag' &&
    css`
      border: none;
      &::after {
        display: none;
      }
    `}
`

const StyledPixelDropDownMenu = styled.div<{ width?: string }>`
  width: ${(props) => (props.width ? props.width : 'auto')};
`

export const PixelDropDownMenu = React.forwardRef<HTMLDivElement, MenuProps>(
  (
    {
      className,
      toggleText = 'new',
      options,
      active,
      variant = 'simple',
      disabled = false,
      margin = '0px',
      maxheight = '250px',
      isGrouped = false,
      width,
      ...rest
    },
    ref,
  ) => {
    const [show, setShow] = React.useState<string | false>(false)
    const [submenuPlacement, setSubmenuPlacement] = React.useState<
      Record<number, 'left' | 'right'>
    >({})
    return (
      <StyledPixelDropDownMenu width={width}>
        <Dropdown>
          <StyledPixelButton
            aria-pressed={active}
            variant={variant}
            ref={ref}
            className={className}
            disabled={disabled}
            margin={margin}
            {...rest}
          >
            {variant === 'tag' ? (
              <DcTag>{toggleText}</DcTag>
            ) : (
              <React.Fragment>{toggleText}</React.Fragment>
            )}
          </StyledPixelButton>

          <Dropdown.Menu
            align="end"
            style={{ width: width, maxHeight: maxheight }}
          >
            {options?.map((data, index) => (
              <DropDownListItem
                isGrouped={isGrouped}
                key={index}
                disabled={data.disabled}
                onClick={(e: React.MouseEvent) => {
                  if (isGrouped) {
                    e.stopPropagation()
                  } else {
                    data.clickHandler && data.clickHandler()
                  }
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                  setShow(data.label)
                  const target = e.currentTarget as HTMLElement
                  const rect = target.getBoundingClientRect()
                  let expectedWidth = 200
                  if (
                    width &&
                    typeof width === 'string' &&
                    width.endsWith('px')
                  ) {
                    const parsed = parseInt(width, 10)
                    if (!isNaN(parsed)) expectedWidth = parsed
                  }
                  const spaceOnRight = window.innerWidth - rect.right
                  const spaceOnLeft = rect.left
                  const placement: 'left' | 'right' =
                    spaceOnRight < expectedWidth && spaceOnLeft >= expectedWidth
                      ? 'left'
                      : 'right'
                  setSubmenuPlacement((prev) => ({
                    ...prev,
                    [index]: placement,
                  }))
                }}
                onMouseLeave={() => setShow(false)}
                active={false}
              >
                {data.children ? '' : data.label}
                {data.children && (
                  <StyledSubDropdown
                    show={show === data.label}
                    $placement={submenuPlacement[index] || 'right'}
                  >
                    <StyledInnerLine
                      aria-pressed={active}
                      variant={variant}
                      className={className}
                      disabled={data.disabled}
                      {...rest}
                    >
                      {data.formatter ? data.formatter(data) : data.label}
                    </StyledInnerLine>

                    <Dropdown.Menu
                      style={{ width: width, maxHeight: maxheight }}
                    >
                      {data.children?.map((data, i) => (
                        <DropDownListItem
                          isGrouped={isGrouped}
                          key={i}
                          disabled={data.disabled}
                          onClick={data.clickHandler}
                        >
                          {data.formatter ? data.formatter(data) : data.label}
                        </DropDownListItem>
                      ))}
                    </Dropdown.Menu>
                  </StyledSubDropdown>
                )}
              </DropDownListItem>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </StyledPixelDropDownMenu>
    )
  },
)

const StyledSubDropdown = styled(Dropdown)<{ $placement?: 'left' | 'right' }>`
  width: 200px;
  font-size: 12px;
  .dropdown-toggle {
    width: 100%;
    text-align: left;

    &::after {
      display: none;
    }
  }
  & .dropdown-menu {
    position: absolute !important;
    top: 0 !important;
    ${(props) =>
      props.$placement === 'left'
        ? css`
            right: 100% !important;
            left: auto !important;
            margin-right: 2px;
            margin-left: 0;
          `
        : css`
            left: 100% !important;
            right: auto !important;
            margin-left: 2px;
            margin-right: 0;
          `}
    transform: none !important;
    max-height: 200px !important;
    overflow: auto !important;
    z-index: 1050;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  }
`

export default PixelDropDownMenu
